// composer/PollBuilder.tsx — inline poll-option editor, swapped in for the media
// zone when the poll tool is active. Mutually exclusive with image attachments
// (enforced by the parent Composer, which disables Add-image while this is open).

import styled from 'styled-components';
import { Icon } from '@/icons';
import type { PollDuration } from '@/core/models/post';

export const MAX_POLL_OPTIONS = 4;
export const MIN_POLL_OPTIONS = 2;
export const MAX_OPTION_LEN = 80;

const DURATIONS: { value: PollDuration; label: string }[] = [
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '24h', label: '1 day' },
  { value: '3d', label: '3 days' },
  { value: '7d', label: '7 days' },
];

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const OptionInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  padding: 6px 0;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  outline: none;

  &:focus-visible {
    border-bottom-color: ${({ theme }) => theme.color.accentCyan};
  }
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;
  padding: 4px;
  border-radius: ${({ theme }) => theme.radius.sm};

  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
    background: ${({ theme }) => theme.color.glassBorder};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const AddRow = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.color.accentCyan};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
`;

const DurationSelect = styled.select`
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  padding: 4px 6px;
`;

const RemovePollBtn = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  cursor: pointer;
  text-decoration: underline;
`;

export interface PollBuilderProps {
  options: string[];
  duration: PollDuration;
  onOptionsChange: (options: string[]) => void;
  onDurationChange: (duration: PollDuration) => void;
  onRemove: () => void;
}

export function PollBuilder({
  options,
  duration,
  onOptionsChange,
  onDurationChange,
  onRemove,
}: PollBuilderProps) {
  function setOption(i: number, value: string) {
    const next = [...options];
    next[i] = value.slice(0, MAX_OPTION_LEN);
    onOptionsChange(next);
  }

  function addOption() {
    if (options.length >= MAX_POLL_OPTIONS) return;
    onOptionsChange([...options, '']);
  }

  function removeOption(i: number) {
    if (options.length <= MIN_POLL_OPTIONS) return;
    onOptionsChange(options.filter((_, j) => j !== i));
  }

  return (
    <Wrap data-testid="poll-builder">
      {options.map((opt, i) => (
        <OptionRow key={i}>
          <OptionInput
            value={opt}
            maxLength={MAX_OPTION_LEN}
            placeholder={`Option ${i + 1}`}
            aria-label={`Poll option ${i + 1}`}
            onChange={(e) => setOption(i, e.target.value)}
          />
          <IconBtn
            type="button"
            aria-label={`Remove option ${i + 1}`}
            disabled={options.length <= MIN_POLL_OPTIONS}
            onClick={() => removeOption(i)}
          >
            <Icon name="close" size={14} />
          </IconBtn>
        </OptionRow>
      ))}
      <AddRow type="button" disabled={options.length >= MAX_POLL_OPTIONS} onClick={addOption}>
        <Icon name="plus" size={14} />
        Add option
      </AddRow>
      <BottomRow>
        <label>
          <DurationSelect
            aria-label="Poll duration"
            value={duration}
            onChange={(e) => onDurationChange(e.target.value as PollDuration)}
          >
            {DURATIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </DurationSelect>
        </label>
        <RemovePollBtn type="button" onClick={onRemove}>
          Remove poll
        </RemovePollBtn>
      </BottomRow>
    </Wrap>
  );
}
